import { api, apiForm } from "./api";

export type FeedbackKind = "bug" | "improvement" | "question";
export type FeedbackPriority = "P0" | "P1" | "P2" | "P3";
export type FeedbackStatus =
  | "new"
  | "needs_info"
  | "reviewing"
  | "planned"
  | "in_progress"
  | "resolved"
  | "closed";

export interface FeedbackAttachment {
  id: string;
  media_type: string;
  size_bytes: number;
  width: number;
  height: number;
  expires_at: string;
}

export interface FeedbackEvent {
  id: string;
  event_type: string;
  visibility: "customer" | "internal";
  message: string | null;
  meta: Record<string, unknown> | null;
  created_at: string;
}

export interface FeedbackTriage {
  id: string;
  model: string;
  summary: string;
  suggested_impact: string | null;
  confidence: number;
  reasoning: string | null;
  tags: string[];
  missing_information: string[];
  duplicate_candidates: Array<Record<string, unknown>>;
  created_at: string;
}

export interface FeedbackItem {
  id: string;
  reference: string;
  workspace_id: string;
  workspace_name: string | null;
  reporter_email: string | null;
  kind: FeedbackKind;
  description: string;
  reported_blocker: boolean;
  status: FeedbackStatus;
  impact: string | null;
  provisional_priority: FeedbackPriority | null;
  confirmed_priority: FeedbackPriority | null;
  priority_reason: Record<string, unknown> | null;
  product_area: string | null;
  acknowledged_at: string | null;
  route: string | null;
  app_version: string | null;
  browser_family: string | null;
  viewport_width: number | null;
  viewport_height: number | null;
  technical_details_included: boolean;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  attachment: FeedbackAttachment | null;
  latest_triage: FeedbackTriage | null;
  events: FeedbackEvent[];
}

export interface FeedbackList {
  items: FeedbackItem[];
  total: number;
  unacknowledged_urgent: number;
  email_alerts_configured: boolean;
}

export interface FeedbackConfig {
  enabled: boolean;
  max_screenshot_mb: number;
  allowed_kinds: FeedbackKind[];
}

export function fetchFeedbackConfig(): Promise<FeedbackConfig> {
  return api<FeedbackConfig>("/api/v1/product-feedback/config");
}

export function submitFeedback(
  workspaceId: string,
  input: {
    kind: FeedbackKind;
    description: string;
    reportedBlocker: boolean;
    technicalDetailsIncluded: boolean;
    screenshot?: Blob | null;
  },
): Promise<FeedbackItem> {
  const form = new FormData();
  form.set("kind", input.kind);
  form.set("description", input.description);
  form.set("reported_blocker", String(input.reportedBlocker));
  form.set("technical_details_included", String(input.technicalDetailsIncluded));
  if (input.technicalDetailsIncluded) {
    form.set("route", window.location.pathname + window.location.search);
    form.set("app_version", import.meta.env.VITE_APP_VERSION ?? "web");
    form.set("browser_family", navigator.userAgent.slice(0, 240));
    form.set("viewport_width", String(window.innerWidth));
    form.set("viewport_height", String(window.innerHeight));
  }
  if (input.screenshot) form.set("screenshot", input.screenshot, "feedback.png");
  return apiForm<FeedbackItem>(`/api/v1/workspaces/${workspaceId}/feedback`, form);
}

export function fetchMyFeedback(workspaceId: string): Promise<FeedbackList> {
  return api<FeedbackList>(`/api/v1/workspaces/${workspaceId}/feedback/mine`);
}

export function fetchFeedbackDetail(workspaceId: string, id: string): Promise<FeedbackItem> {
  return api<FeedbackItem>(`/api/v1/workspaces/${workspaceId}/feedback/${id}`);
}

export function replyToFeedback(workspaceId: string, id: string, message: string) {
  return api<FeedbackItem>(`/api/v1/workspaces/${workspaceId}/feedback/${id}/reply`, {
    method: "POST",
    body: { message },
  });
}

export function fetchAdminFeedback(filters: Record<string, string> = {}): Promise<FeedbackList> {
  const query = new URLSearchParams(filters);
  return api<FeedbackList>(`/api/v1/admin/feedback?${query.toString()}`);
}

export function fetchAdminFeedbackDetail(id: string): Promise<FeedbackItem> {
  return api<FeedbackItem>(`/api/v1/admin/feedback/${id}`);
}

export function acknowledgeFeedback(id: string) {
  return api<{ acknowledged_at: string; acknowledged_by: string }>(
    `/api/v1/admin/feedback/${id}/acknowledge`,
    { method: "POST" },
  );
}

export function updateFeedback(id: string, body: Partial<{
  status: FeedbackStatus;
  impact: string;
  product_area: string;
  owner_user_id: string;
}>) {
  return api<FeedbackItem>(`/api/v1/admin/feedback/${id}`, { method: "PATCH", body });
}

export function setFeedbackPriority(id: string, priority: FeedbackPriority, reason: string) {
  return api<FeedbackItem>(`/api/v1/admin/feedback/${id}/priority`, {
    method: "POST",
    body: { priority, reason },
  });
}

export function addFeedbackNote(id: string, message: string, customerVisible: boolean) {
  return api<FeedbackItem>(`/api/v1/admin/feedback/${id}/notes`, {
    method: "POST",
    body: { message, customer_visible: customerVisible },
  });
}

export function retriageFeedback(id: string) {
  return api<FeedbackItem>(`/api/v1/admin/feedback/${id}/triage`, { method: "POST" });
}
